/**
 * Tournament Configuration — Section 06, `Appointment Rules` (module 07).
 *
 * Authority: `07-configuration.md` Section 06 (`Rules In Force`, the `Country Conflict`
 * sub-toggle and `Tunable Thresholds`) and `apps/api/src/routes/configuration.ts`, which is
 * the data layer for exactly this section and no more of the page.
 *
 * Four rules this component exists to keep:
 *
 *  1. **This section stays editable after go-live, and it is the only one that does.**
 *     Sections 01–05 lock; Section 06 does not, because "a rule that proves too strict
 *     mid-pool can be relaxed between matchdays without restarting the tournament". So every
 *     control here is live-bound: there is no Save-the-page button, no Reset and no modal
 *     (§3), only a per-field commit.
 *
 *  2. **Nothing is optimistic.** The marking panel and the appointments panel both move the
 *     control first and reconcile after, because a bar that lags feels broken. A rule set
 *     does not get that latitude: a switch that reads `On` while the engine still has it
 *     `Off` would mean the next draft breaks a rule the TD believes is in force. So the
 *     control is disabled while the write is in flight and re-rendered from the server's
 *     own reply — the whole section comes back on every write for this reason.
 *
 *  3. **Stored and resolved are different facts, shown as two.** A threshold that has never
 *     been set is ABSENT from the payload, and the API keeps it absent deliberately so the
 *     field can render blank rather than as the default. Beside it the row states what the
 *     engine will actually use. Collapsing the two is how a TD comes to believe a value is
 *     configured when it is not.
 *
 *  4. **A refusal is stated where it happened.** A 422 — switching off a rule the schema
 *     marks `always_on` — is the API's own sentence, printed on that rule's row. Nothing is
 *     paraphrased and nothing retries.
 */

import { useState } from "react";

import { ApiError, patchRule, putThreshold } from "../../api/client.js";
import type { AppointmentRuleView, ConfigurationViewBody, ThresholdKey } from "../../api/types.js";
import {
  aiHardness,
  hasAiHardnessSubToggle,
  ruleKindClass,
  ruleKindLabel,
  rulesInForce,
  thresholdCopy,
} from "./vocabulary.js";

/**
 * The two writes, behind one seam. The real implementations are the API client's; the
 * indirection exists so a component test can drive the panel without a network, not so the
 * transport can be swapped at runtime.
 */
export interface ConfigurationWriter {
  setThreshold: (key: ThresholdKey, value: string) => Promise<ConfigurationViewBody>;
  setRule: (
    code: string,
    body: { enabled?: boolean; aiDraftsTreatAsHard?: boolean },
  ) => Promise<ConfigurationViewBody>;
}

export const LIVE_CONFIGURATION_WRITER: ConfigurationWriter = {
  setThreshold: putThreshold,
  setRule: patchRule,
};

export interface ConfigurationPanelProps {
  /**
   * The section as fetched. Held as seed state, which is safe because there is one
   * configuration per installation — this component is never re-pointed at a different one.
   */
  initialView: ConfigurationViewBody;
  writer?: ConfigurationWriter;
  /**
   * Called after any write the server accepted. The change log is a separate fetch, and a
   * change that has just been made must appear in it without a reload.
   */
  onWritten?: () => void;
}

/** Exactly one control may be in flight, and it is named so the others can say why. */
type Pending =
  | { kind: "threshold"; key: string }
  | { kind: "rule"; code: string; field: "enabled" | "aiDraftsTreatAsHard" }
  | null;

interface RowError {
  row: string;
  message: string;
}

/** The row key both the pending state and the error state address a control by. */
function thresholdRow(key: string): string {
  return `threshold:${key}`;
}
function ruleRow(code: string): string {
  return `rule:${code}`;
}

export function ConfigurationPanel({
  initialView,
  writer = LIVE_CONFIGURATION_WRITER,
  onWritten,
}: ConfigurationPanelProps): React.JSX.Element {
  const [view, setView] = useState<ConfigurationViewBody>(initialView);
  const [pending, setPending] = useState<Pending>(null);
  const [rowError, setRowError] = useState<RowError | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /** Per-key text the TD has typed but not yet committed. Absent means "as stored". */
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const busy = pending !== null;

  /** Whatever the write, the answer replaces the section outright. */
  function accept(next: ConfigurationViewBody, message: string): void {
    setView(next);
    setNote(message);
    setRowError(null);
    onWritten?.();
  }

  function refuse(row: string, error: unknown, fallback: string): void {
    setNote(null);
    setRowError({
      row,
      message: error instanceof ApiError ? error.message : fallback,
    });
  }

  async function commitThreshold(key: ThresholdKey, value: string): Promise<void> {
    const row = thresholdRow(key);
    setPending({ kind: "threshold", key });
    setRowError(null);
    setNote(null);
    try {
      const next = await writer.setThreshold(key, value);
      // The draft is dropped rather than kept: the field now shows what was stored, which
      // may differ from what was typed (the API trims), and the stored value is the truth.
      setDrafts((current) =>
        Object.fromEntries(Object.entries(current).filter(([entry]) => entry !== key)),
      );
      accept(next, `${thresholdCopy(key).label} saved.`);
    } catch (error) {
      refuse(row, error, "The threshold was not saved. The stored value is unchanged.");
    } finally {
      setPending(null);
    }
  }

  async function commitRule(
    rule: AppointmentRuleView,
    field: "enabled" | "aiDraftsTreatAsHard",
    value: boolean,
  ): Promise<void> {
    const row = ruleRow(rule.code);
    setPending({ kind: "rule", code: rule.code, field });
    setRowError(null);
    setNote(null);
    try {
      const next = await writer.setRule(
        rule.code,
        field === "enabled" ? { enabled: value } : { aiDraftsTreatAsHard: value },
      );
      accept(
        next,
        field === "enabled"
          ? `${rule.title} switched ${value ? "on" : "off"}.`
          : value
            ? "AI drafts treat country conflict as hard · the engine will never propose a same-country appointment."
            : "AI drafts may propose same-country appointments · they will be flagged for review.",
      );
    } catch (error) {
      refuse(row, error, "The rule was not changed. It is unchanged on the server.");
    } finally {
      setPending(null);
    }
  }

  return (
    <>
      <section className="panel panel--d1" aria-labelledby="cfgRulesTitle" data-howto="rules-in-force">
        <div className="panel__head">
          <h3 className="panel__title" id="cfgRulesTitle">
            Rules In Force
          </h3>
          <span className="panel__note">Stays Editable · Tunable Between Matchdays</span>
          <span className="spacer" />
          <span className="statuspill statuspill--good" data-testid="rules-in-force">
            <span className="sdot" aria-hidden="true" />
            {rulesInForce(view.rules)} of {view.rules.length} in force
          </span>
        </div>

        {note === null ? null : (
          <p className="apptnote" role="status" data-testid="config-note">
            {note}
          </p>
        )}

        {view.rules.length === 0 ? (
          <div className="emptystate">
            <b>No Rules Seeded</b>
            This tournament carries no appointment rules yet, so the drafting engine is running
            on its own defaults. Nothing here has been switched off — nothing has been set.
          </div>
        ) : (
          <div data-testid="rule-list">
            {view.rules.map((rule) => (
              <RuleRow
                key={rule.code}
                rule={rule}
                busy={busy}
                saving={pending?.kind === "rule" && pending.code === rule.code}
                error={rowError?.row === ruleRow(rule.code) ? rowError.message : null}
                engineTreatsAsHard={view.engineConfig.country_conflict_hard_for_drafts}
                onToggle={(field, value) => void commitRule(rule, field, value)}
              />
            ))}
          </div>
        )}

        <div className="panel__foot">
          <b>Stays editable throughout.</b> Every change is applied to future drafts only —
          appointments already published to TMS are left alone. An <b>Always On</b> rule has no
          switch because the tournament&rsquo;s own rule set refuses to unset it.
        </div>
      </section>

      <section className="panel panel--d1 cfgpanel" aria-labelledby="cfgThreshTitle" data-howto="tunable-thresholds">
        <div className="panel__head">
          <h3 className="panel__title" id="cfgThreshTitle">
            Tunable Thresholds
          </h3>
          <span className="panel__note">Stored Value Beside What Will Actually Be Used</span>
        </div>

        <table className="mtable" aria-label="Tunable thresholds">
          <thead>
            <tr>
              <th scope="col">Threshold</th>
              <th scope="col">Stored</th>
              <th scope="col">Will Be Used</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {view.configurableThresholdKeys.map((key) => (
              <ThresholdRow
                key={key}
                thresholdKey={key}
                stored={view.thresholds[key] ?? null}
                resolved={resolvedThreshold(view, key)}
                draft={drafts[key]}
                busy={busy}
                saving={pending?.kind === "threshold" && pending.key === key}
                error={rowError?.row === thresholdRow(key) ? rowError.message : null}
                onDraft={(value) => {
                  setDrafts((current) => ({ ...current, [key]: value }));
                }}
                onCommit={(value) => void commitThreshold(key, value)}
              />
            ))}
          </tbody>
        </table>

        <div className="panel__foot">
          <b>Blank is not zero.</b> A threshold that has never been set is shown empty, and
          whatever reads it falls back to its own default — the figure in the third column. The
          two are different facts and the page keeps them apart.
          <br />
          <b>The daily report&rsquo;s two settings</b> decide when the Consolidated Daily UM
          Debrief Report is built: the last match of the day is treated as finishing{" "}
          {String(view.dailyReport.matchLengthMinutes)} minutes after it starts, the report is
          built the configured delay after that, and it never waits past the backstop. On a day
          with no matches, no report is built at all.
        </div>
      </section>
    </>
  );
}

/**
 * What the engine will actually use for a key, where the engine takes it at all.
 *
 * `umpire_onfield_per_day` has no `EngineConfig` field — it is the umpire side's threshold
 * and the technical drafting engine does not read it — so the row says so rather than
 * printing a number the engine never consults.
 */
function resolvedThreshold(view: ConfigurationViewBody, key: string): number | string | null {
  const engine = view.engineConfig;
  switch (key) {
    /**
     * RULING R-D6's two settings are read by the nightly report build and by no drafting
     * engine, so the third column prints what the BUILD will use — the same "stored beside
     * resolved" idea, for a different reader.
     */
    case "daily_report_delay_minutes":
      return `${String(view.dailyReport.delayMinutes)} min`;
    case "daily_report_backstop_time":
      return view.dailyReport.backstopTime;
    case "judge_sj_tj_tolerance":
      return engine.judge_sj_tj_tolerance;
    case "team_exposure_heat":
      return engine.team_exposure_heat;
    case "to_appointments_per_day":
      return engine.to_appointments_per_day;
    case "co_appointment_pair_tolerance":
      return engine.co_appointment_pair_tolerance;
    default:
      return null;
  }
}

/* ── One rule ─────────────────────────────────────────────────────────────── */

interface RuleRowProps {
  rule: AppointmentRuleView;
  busy: boolean;
  saving: boolean;
  error: string | null;
  /** The engine's resolved hardness, which an off rule collapses to `false`. */
  engineTreatsAsHard: boolean;
  onToggle: (field: "enabled" | "aiDraftsTreatAsHard", value: boolean) => void;
}

function RuleRow({
  rule,
  busy,
  saving,
  error,
  engineTreatsAsHard,
  onToggle,
}: RuleRowProps): React.JSX.Element {
  const switchLabel = `${rule.title} in force`;
  const subToggleLabel = `AI drafts treat ${rule.title} as hard`;

  return (
    <div className={`cfgrule${saving ? " is-saving" : ""}`} data-testid={`rule-${rule.code}`}>
      <div className="cfgrule__head">
        <span className={ruleKindClass(rule.kind)}>{ruleKindLabel(rule.kind)}</span>
        <span className="cfgrule__title">{rule.title}</span>
        {rule.tdAdded ? <span className="editchip">TD Added</span> : null}
        <span className="spacer" />
        {rule.alwaysOn ? (
          <span className="statuspill statuspill--muted" data-testid={`always-on-${rule.code}`}>
            <span className="sdot" aria-hidden="true" />
            Always On
          </span>
        ) : (
          <label className={`natoggle${rule.enabled ? " is-on" : ""}`}>
            <input
              type="checkbox"
              aria-label={switchLabel}
              checked={rule.enabled}
              disabled={busy}
              onChange={(event) => {
                onToggle("enabled", event.target.checked);
              }}
            />
            {rule.enabled ? "On" : "Off"}
          </label>
        )}
      </div>

      <p className="cfgrule__desc">{rule.description}</p>
      {rule.note === null ? null : <p className="critnote">{rule.note}</p>}

      {hasAiHardnessSubToggle(rule) ? (
        <div className="cfgsub" data-testid={`ai-hard-${rule.code}`}>
          <label className={`natoggle${aiHardness(rule) ? " is-on" : ""}`}>
            <input
              type="checkbox"
              aria-label={subToggleLabel}
              checked={aiHardness(rule)}
              disabled={busy}
              onChange={(event) => {
                onToggle("aiDraftsTreatAsHard", event.target.checked);
              }}
            />
            AI Drafts Treat As Hard
          </label>
          <span className="cfgsub__body">
            {aiHardness(rule)
              ? "The drafting engine never proposes a same-country appointment; only the TD can make one, by hand."
              : "The drafting engine may propose a same-country appointment and flag it for review."}{" "}
            Override is a deliberate two-step action and the row stays flagged afterwards.
          </span>
          {rule.enabled ? null : (
            <span className="cfgsub__body" data-testid={`ai-hard-inert-${rule.code}`}>
              With the rule switched off the engine treats country conflict as{" "}
              {engineTreatsAsHard ? "hard" : "no constraint at all"}, whatever this sub-toggle
              says.
            </span>
          )}
        </div>
      ) : null}

      {error === null ? null : (
        <span className="rowerror" role="alert" data-testid={`rule-error-${rule.code}`}>
          {error}
        </span>
      )}
    </div>
  );
}

/* ── One threshold ────────────────────────────────────────────────────────── */

interface ThresholdRowProps {
  thresholdKey: string;
  stored: string | null;
  resolved: number | string | null;
  draft: string | undefined;
  busy: boolean;
  saving: boolean;
  error: string | null;
  onDraft: (value: string) => void;
  onCommit: (value: string) => void;
}

function ThresholdRow({
  thresholdKey,
  stored,
  resolved,
  draft,
  busy,
  saving,
  error,
  onDraft,
  onCommit,
}: ThresholdRowProps): React.JSX.Element {
  const copy = thresholdCopy(thresholdKey);
  const shown = draft ?? stored ?? "";
  // Dirty against what is STORED, not against the last keystroke, so retyping the stored
  // value puts the row back to rest rather than offering a write that would change nothing.
  const dirty = shown !== (stored ?? "");

  return (
    <tr data-testid={`threshold-${thresholdKey}`} className={saving ? "is-saving" : undefined}>
      <th scope="row">
        <span className="cfgthresh__label">{copy.label}</span>
        <span className="critnote">{copy.hint}</span>
      </th>

      <td>
        <input
          className="cfgthresh__input num"
          type="text"
          inputMode={copy.inputMode ?? "numeric"}
          aria-label={copy.label}
          value={shown}
          disabled={busy}
          onChange={(event) => {
            onDraft(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && dirty && !busy) onCommit(shown);
          }}
        />
        {stored === null ? (
          <span className="critnote" data-testid={`threshold-unset-${thresholdKey}`}>
            Never set
          </span>
        ) : null}
      </td>

      <td className="num">
        {resolved === null ? (
          <span className="btnhint" data-testid={`threshold-not-engine-${thresholdKey}`}>
            Not read by the technical engine
          </span>
        ) : (
          resolved
        )}
      </td>

      <td>
        {/*
          The label names its field. Four rows carry a Save, and four controls called "Save"
          are four controls a screen reader cannot tell apart — and a test cannot either,
          which is the same defect showing up earlier.
        */}
        <button
          type="button"
          className="gbtn"
          aria-label={`Save ${copy.label}`}
          data-testid={`threshold-save-${thresholdKey}`}
          disabled={busy || !dirty}
          aria-busy={saving}
          onClick={() => {
            onCommit(shown);
          }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        {error === null ? null : (
          <span className="rowerror" role="alert" data-testid={`threshold-error-${thresholdKey}`}>
            {error}
          </span>
        )}
      </td>
    </tr>
  );
}
